import { put, takeLatest } from "redux-saga/effects";
import {
    getBookingsSuccess,
    getBookingsFailed,
    getBookingByIdSuccess,
    getBookingByIdFailed,
    getBookingByShowIdSuccess,
    getBookingByShowIdFailed,
    getBookingByShortIdSuccess,
    getBookingByShortIdFailed,
    createManualBookingSuccess,
    createManualBookingFailed,
    updateBookingStatusSuccess,
    updateBookingStatusFailed,
    updateBookingSuccess,
    updateBookingFailed,
    cancelBookingSuccess,
    cancelBookingFailed,
    deleteBookingSuccess,
    deleteBookingFailed,
    getBookingsByDateRangeSuccess,
    getBookingsByDateRangeFailed,
    checkInSuccess,
    checkInFailed,
    importBookingsSuccess,
    importBookingsFailed,
    updateJobStatus,
    deleteBookingPaymentFailed,
    deleteBookingPaymentSuccess,
} from "../reducers/bookingSlice";
import { axiosInstance } from "../../config/axios";
import { apiEndpoints } from "../../utils/api-endpoints";
import { createErrorAction } from '../reducers/errorSlice';
import { getErrorMessage } from '../../utils/helper.utils';
import { delay } from 'redux-saga/effects';

function* getBookings(action: any): any {
    try {
        const raw = action.payload || {};
        const params = {
            ...raw,
            payment_status: Array.isArray(raw.payment_status)
                ? raw.payment_status.join(',')
                : raw.payment_status ?? ''
        };
        const response = yield axiosInstance.get(apiEndpoints.booking.list, { params });
        yield put(getBookingsSuccess(response.data));
    } catch (error) {
        yield put(getBookingsFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'booking/getBookings'));
    }
}

function* getBookingById(action: any): any {
    try {
        const response = yield axiosInstance.get(apiEndpoints.booking.byId(action.payload));
        yield put(getBookingByIdSuccess(response.data));
    } catch (error) {
        yield put(getBookingByIdFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'booking/getBookingById'));
    }
}

function* getBookingByShowId(action: any): any {
    try {
        const response = yield axiosInstance.get(apiEndpoints.booking.byShowId(action.payload));
        yield put(getBookingByShowIdSuccess(response.data));
    } catch (error) {
        yield put(getBookingByShowIdFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'booking/getBookingByShowId'));
    }
}

function* getBookingByShortId(action: any): any {
    try {
        const response = yield axiosInstance.get(apiEndpoints.booking.byShortId(action.payload));
        yield put(getBookingByShortIdSuccess(response.data));
    } catch (error) {
        yield put(getBookingByShortIdFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'booking/getBookingByShortId'));
    }
}

function* createManualBooking(action: any): any {
    try {
        const response = yield axiosInstance.post(apiEndpoints.booking.manualCreate, action.payload);
        yield put(createManualBookingSuccess(response.data));
    } catch (error) {
        yield put(createManualBookingFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'booking/createManualBooking'));
    }
}

function* updateBookingStatus(action: any): any {
    try {
        const { id, status } = action.payload;
        const response = yield axiosInstance.put(apiEndpoints.booking.updateStatus(id), { status });
        yield put(updateBookingStatusSuccess(response.data));
    } catch (error) {
        yield put(updateBookingStatusFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'booking/updateBookingStatus'));
    }
}

function* updateBooking(action: any): any {
    try {
        const { id, data } = action.payload;
        const response = yield axiosInstance.put(apiEndpoints.booking.update(id), data);
        yield put(updateBookingSuccess(response.data));
    } catch (error) {
        yield put(updateBookingFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'booking/updateBooking'));
    }
}

function* cancelBooking(action: any): any {
    try {
        const { id, updateShowCapacity } = action.payload;
        const response = yield axiosInstance.post(apiEndpoints.booking.cancel(id), { updateShowCapacity });
        yield put(cancelBookingSuccess(response.data));
    } catch (error) {
        yield put(cancelBookingFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'booking/cancelBooking'));
    }
}

function* deleteBooking(action: any): any {
    try {
        const { id } = action.payload;
        yield axiosInstance.delete(apiEndpoints.booking.delete(id));
        yield put(deleteBookingSuccess(id));
    } catch (error) {
        yield put(deleteBookingFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'booking/deleteBooking'));
    }
}

function* getBookingsByDateRange(action: any): any {
    try {
        const { startDate, endDate } = action.payload;
        const response = yield axiosInstance.get(apiEndpoints.booking.dateRange, {
            params: { startDate, endDate }
        });
        yield put(getBookingsByDateRangeSuccess(response.data));
    } catch (error) {
        yield put(getBookingsByDateRangeFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'booking/getBookingsByDateRange'));
    }
}

function* checkIn(action: any): any {
    try {
        const { id, token } = action.payload;
        const response = yield axiosInstance.get(apiEndpoints.booking.checkIn(id, token));
        yield put(checkInSuccess(response.data));
    } catch (error) {
        yield put(checkInFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'booking/checkIn'));
    }
}

function* importBookings(action: any): any {
    try {
        const response = yield axiosInstance.post(apiEndpoints.booking.import, { bookings: action.payload });
        yield put(importBookingsSuccess(response.data));

        const jobId = response.data.jobId;
        while (true) {
            const jobResponse = yield axiosInstance.get(apiEndpoints.booking.jobStatus(jobId));
            yield put(updateJobStatus(jobResponse.data));
            
            if (['completed', 'failed'].includes(jobResponse.data.status)) {
                break;
            }
            yield delay(2000);
        }
    } catch (error) {
        yield put(importBookingsFailed());
    }
}

function* deleteBookingPayment(action: any): any {
    try {
        const response = yield axiosInstance.post(apiEndpoints.booking.deletePayment(action.payload));
        yield put(deleteBookingPaymentSuccess(response.data));
    } catch (error) {
        yield put(deleteBookingPaymentFailed(getErrorMessage(error)));
        yield put(createErrorAction(error, 'booking/deleteBookingPayment'));
    }
}

    export function* watchBookingSaga() {
    yield takeLatest('booking/getBookings', getBookings);
    yield takeLatest('booking/getBookingById', getBookingById);
    yield takeLatest('booking/getBookingByShowId', getBookingByShowId);
    yield takeLatest('booking/getBookingByShortId', getBookingByShortId);
    yield takeLatest('booking/createManualBooking', createManualBooking);
    yield takeLatest('booking/updateBookingStatus', updateBookingStatus);
    yield takeLatest('booking/updateBooking', updateBooking);
    yield takeLatest('booking/cancelBooking', cancelBooking);
    yield takeLatest('booking/deleteBooking', deleteBooking);
    yield takeLatest('booking/getBookingsByDateRange', getBookingsByDateRange);
    yield takeLatest('booking/checkIn', checkIn);
    yield takeLatest('booking/importBookings', importBookings);
    yield takeLatest('booking/deleteBookingPayment', deleteBookingPayment);
}